import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, AutoComplete, Button, Checkbox, Col, Divider, Form, Input, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/Agent/AgentModel';
import AgentService from '../../../services/agent/AgentService';
import { RootState } from '../../../store';
import useWindowSize from '../../../utils/useWindowSize';

const { Option } = Select;

const AgentForm: React.FC<any> = ({}) => {
  const [form] = useForm();
  const dispatch = useDispatch();
  const width = useWindowSize();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  const agent = useSelector((state: RootState) => state.models.agent);
  const companies = useSelector((state: RootState) => state?.store?.company?.data);
  const workPlaces = useSelector((state: RootState) => state?.store?.workPlace?.data).filter(
    (w: any) => w.companyId == agent?.companyId
  );

  const initialValues = {
    id: useSelector((state: RootState) => state?.models?.agent?.id),
    name: useSelector((state: RootState) => state?.models?.agent?.name),
    phone: useSelector((state: RootState) => state?.models?.agent?.phone),
    email: useSelector((state: RootState) => state?.models?.agent?.email),
    role: useSelector((state: RootState) => state?.models?.agent?.role),
    company: useSelector((state: RootState) => state?.models?.agent?.company),
    companyId: useSelector((state: RootState) => state?.models?.agent?.companyId),
    workPlace: useSelector((state: RootState) => state?.models?.agent?.workPlace),
    workPlaceId: useSelector((state: RootState) => state?.models?.agent?.workPlaceId),
    isActive: useSelector((state: RootState) => state?.models?.agent?.isActive),
    isDefaultOnInterventionFile: useSelector((state: RootState) => state?.models?.agent?.isDefaultOnInterventionFile),
  };

  const [searchTerm, setSearchTerm] = useState<string>('');

  const { mutate: saveAgentType, isPending: _isSavingLabor } = useMutation({
    mutationFn: (formData: any) => AgentService.upsert(agent.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !agent.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const handleWorkPlace = useCallback(
    (value: string): void => {
      const workPlace = workPlaces?.find((w: any) => w?.name === value);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        workPlaceId: workPlace?.id,
        workPlace: workPlace,
      });

      dispatch(updateField({ field: 'workPlace', value: workPlace ?? null }));
      dispatch(updateField({ field: 'workPlaceId', value: workPlace?.id ?? null }));

      // Only trigger the search for contracts after the state is updated
    },
    [agent, workPlaces]
  );

  const handleSelectCompany = useCallback(
    (value: string): void => {
      const company = companies?.find((c) => c.name === value);
      form.setFieldsValue({
        ...form.getFieldsValue(true),
        companyId: company?.id,
        company: company,
      });

      dispatch(updateField({ field: 'company', value: company ?? null }));
      dispatch(updateField({ field: 'companyId', value: company?.id ?? null }));
    },
    [agent, companies]
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldsValue = {
      ...form.getFieldsValue(true),
      isActive: e.target.checked,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleDefaultInterventionFileCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldsValue = {
      ...form.getFieldsValue(true),
      isDefaultOnInterventionFile: e.target.checked,
    };
    form.setFieldsValue(fieldsValue);
  };

  const onSaveAgentType = async () => {
    saveAgentType(agent);
  };

  const handleFilter = useCallback((inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  }, []);

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  return (
    <Form
      form={form}
      name='agentForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={onSaveAgentType}
      layout='vertical'
    >
      <Divider />
      <Row gutter={[16, 16]} className='row'>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item
            label='Client'
            name={['company', 'name']}
            rules={[{ required: true, message: 'Alegeti un client!' }]}
          >
            <AutoComplete
              placeholder='Cauta firma...'
              onSelect={handleSelectCompany}
              onChange={(value: string) => handleSearchTermChange(value)}
              filterOption={handleFilter}
              value={searchTerm}
              allowClear
            >
              {companies?.map((company, index) => (
                <Option key={index} value={company?.name}>
                  {company?.name}
                </Option>
              ))}
            </AutoComplete>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item
            label='Punct de lucru'
            name={['workPlace', 'name']}
            rules={[{ required: true, message: 'Alegeti un punct de lucru!' }]}
          >
            <AutoComplete
              placeholder='Cauta punct de lucru'
              onSelect={handleWorkPlace}
              onChange={(value: string) => handleSearchTermChange(value)}
              filterOption={handleFilter}
              value={searchTerm}
              allowClear
            >
              {workPlaces?.map((workPlace: any, index: any) => (
                <Option key={index} value={workPlace?.name}>
                  {workPlace?.name}
                </Option>
              ))}
            </AutoComplete>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Denumire' name='name' rules={[{ required: true, message: 'Introdu denumire!' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Telefon' name='phone' rules={[{ required: true, message: 'Introdu telefon!' }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 16]} className='row'>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Email' name='email' rules={[{ required: true, message: 'Introdu email!' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Rol' name='role' rules={[{ required: true, message: 'Introdu rol!' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Default pe fisa de interventie' name='isDefaultOnInterventionFile' valuePropName='checked'>
            <Checkbox onChange={() => handleCheckboxChange} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
          <Form.Item label='Status' name='isActive' valuePropName='checked'>
            <Checkbox onChange={() => handleDefaultInterventionFileCheckboxChange} />
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              Finalizare
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default AgentForm;
